import React, { useMemo } from 'react';
import { Select, Switch, Space } from 'antd';
import { FileOutlined, UserOutlined } from '@ant-design/icons';
import ProjectSubmissionModal from '@/components/module/projects/ProjectSubmissionModal';
import MilestoneLinkModal from '@/components/module/projects/milestone_tab/MilestoneLinkModal';
import { PendingAttachmentTile, PendingLinkWrap } from '@/components/module/projects/sprints_tab/PendingRichTextAttachments';
import { PanelSectionLabel } from '@/pages/inside/tenant/style';
import {
    getSprintTaskApiId,
    getSprintTaskNumberLabel,
    getTaskDescriptionMarkdown,
    truncateFileName,
} from '@hooks/projects/projectHelpers';

/**
 * Task Consultation Modal: Select consultants and tasks for architectural/technical input.
 */
export default function TaskConsultationModal({
    open,
    onCancel,
    allSprints = [],
    addToConsultation,
    submissionGroupedBySprintEnriched,
    consultationNotes,
    setConsultationNotes,
    consultantIds,
    setConsultantIds,
    qaPassed,
    setQaPassed,
    projectMembers = [],
    submitConsultation,
    submitPending,
    removeFromConsultation,
    onOpenTaskDetail,
    mentionUsers = [],
    mentionMilestones = [],
    mentionSprints = [],
    consultationPendingFiles = [],
    consultationPendingLinks = [],
    handleConsultationFilesSelected,
    handleConsultationLinkRequest,
    handleConsultationLinkModalSubmit,
    consultationLinkModalOpen,
    closeConsultationLinkModal,
    consultationLinkForm,
    removeConsultationPendingFile,
    removeConsultationPendingLink,
    handleConsultationEditorLinkRemoved,
}) {
    const taskRows = useMemo(() => {
        const groups = Array.isArray(submissionGroupedBySprintEnriched) ? submissionGroupedBySprintEnriched : [];
        const rows = [];
        for (const g of groups) {
            const sprint = g.fullSprint;
            const tasks = Array.isArray(g.fullTasks) ? g.fullTasks : [];
            for (const task of tasks) {
                const id = getSprintTaskApiId(task) ?? task?.id;
                if (id == null) continue;
                const descriptionMarkdown = getTaskDescriptionMarkdown(task);
                rows.push({
                    key: `${g.sprintId}-${id}`,
                    taskIdLabel: getSprintTaskNumberLabel(task),
                    title: task?.title ?? task?.name ?? '—',
                    ...(descriptionMarkdown ? { descriptionMarkdown } : {}),
                    removeType: 'task',
                    removeKey: id,
                    payload: { sprint, task },
                });
            }
        }
        return rows;
    }, [submissionGroupedBySprintEnriched]);

    const handleTaskClick = (payload) => {
        if (!payload?.task || !onOpenTaskDetail) return;
        onOpenTaskDetail(payload.sprint, payload.task);
    };

    const richTextEditorProps = useMemo(
        () => ({
            showAttachButton: true,
            onFilesSelected: handleConsultationFilesSelected,
            onLinkRequest: handleConsultationLinkRequest,
            onLinkRemoved: typeof handleConsultationEditorLinkRemoved === 'function' ? handleConsultationEditorLinkRemoved : undefined,
            onAttachmentRemoved:
                typeof removeConsultationPendingFile === 'function'
                    ? (index) => {
                          const p = consultationPendingFiles[index];
                          if (p) removeConsultationPendingFile(p.id);
                      }
                    : undefined,
            attachmentIndexOffset: consultationPendingFiles.length,
        }),
        [
            handleConsultationFilesSelected,
            handleConsultationLinkRequest,
            handleConsultationEditorLinkRemoved,
            removeConsultationPendingFile,
            consultationPendingFiles,
        ],
    );

    const attachmentsBlock = useMemo(() => {
        const files = Array.isArray(consultationPendingFiles) ? consultationPendingFiles : [];
        const links = Array.isArray(consultationPendingLinks) ? consultationPendingLinks : [];
        if (files.length === 0 && links.length === 0) return null;

        return (
            <div
                style={{
                    marginTop: 8,
                    marginBottom: 16,
                    padding: '8px 0',
                    borderTop: '1px solid var(--ant-color-border, #f0f0f0)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0,
                }}
            >
                {files.length > 0 ? (
                    <>
                        <PanelSectionLabel
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 6,
                                marginBottom: 8,
                                fontSize: 11,
                                textTransform: 'uppercase',
                                letterSpacing: '0.04em',
                                opacity: 0.75,
                            }}
                        >
                            <FileOutlined />
                            Attachments
                        </PanelSectionLabel>
                        <div
                            style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
                                gap: 10,
                            }}
                        >
                            {files.map((p) => (
                                <PendingAttachmentTile
                                    key={p.id}
                                    item={p}
                                    onRemove={() => removeConsultationPendingFile?.(p.id)}
                                    truncateFileName={truncateFileName}
                                    showBottomFileName={false}
                                />
                            ))}
                        </div>
                    </>
                ) : null}
                {links.length > 0 ? (
                    <div style={{ marginTop: files.length > 0 ? 12 : 0 }}>
                        <PendingLinkWrap links={links} onRemoveLink={removeConsultationPendingLink} />
                    </div>
                ) : null}
            </div>
        );
    }, [consultationPendingFiles, consultationPendingLinks, removeConsultationPendingFile, removeConsultationPendingLink]);

    const excludedUserIds = useMemo(() => {
        const ids = new Set();
        for (const row of taskRows) {
            const task = row.payload?.task;
            if (!task) continue;

            const creator = task.createdBy ?? task.created_by;
            if (creator) {
                if (typeof creator === 'object') {
                    if (creator.id) ids.add(String(creator.id));
                    else if (creator._id) ids.add(String(creator._id));
                } else {
                    ids.add(String(creator));
                }
            }

            const assignee = task.assigneeId ?? task.assignee_id ?? task.assignee;
            if (assignee) {
                if (typeof assignee === 'object') {
                    if (assignee.id) ids.add(String(assignee.id));
                    else if (assignee._id) ids.add(String(assignee._id));
                } else {
                    ids.add(String(assignee));
                }
            }
        }
        return ids;
    }, [taskRows]);

    const isCompanyOwnerOrAdmin = (m) => {
        if (!m) return false;
        if (m.isOwner === true || m.isAdmin === true || m.admin === true || m.is_admin === true || m.is_owner === true) {
            return true;
        }
        const roles = [];
        if (m.role) {
            if (typeof m.role === 'object') {
                roles.push(m.role.name ?? m.role.displayName ?? m.role.id ?? '');
            } else if (typeof m.role === 'string') {
                roles.push(m.role);
            }
        }
        if (m.roleName) {
            roles.push(m.roleName);
        }
        if (Array.isArray(m.roles)) {
            m.roles.forEach(r => {
                if (typeof r === 'object') {
                    roles.push(r.name ?? r.displayName ?? r.id ?? '');
                } else if (typeof r === 'string') {
                    roles.push(r);
                }
            });
        }
        const adminKeywords = ['admin', 'owner', 'super-admin', 'sub-admin', 'superadmin'];
        return roles.some(role => 
            adminKeywords.some(keyword => String(role).toLowerCase().includes(keyword))
        );
    };

    const consultantOptions = useMemo(() => {
        return projectMembers
            .filter((m) => {
                if (!m || m.id == null) return false;
                const midStr = String(m.id);
                if (excludedUserIds.has(midStr)) return false;
                if (isCompanyOwnerOrAdmin(m)) return false;
                return true;
            })
            .map((m) => ({
                value: m.id,
                label: m.name || m.email || 'User',
            }));
    }, [projectMembers, excludedUserIds]);

    const availableTasks = useMemo(() => {
        const sprints = Array.isArray(allSprints) ? allSprints : [];
        const selectedIds = new Set(taskRows.map(r => r.removeKey));
        const list = [];
        for (const s of sprints) {
            const tasks = Array.isArray(s.tasks) ? s.tasks : [];
            for (const t of tasks) {
                const tid = getSprintTaskApiId(t) ?? t.id;
                const status = String(t?.status ?? '').toLowerCase();
                const isTodo = status === 'todo' || status === 'pending' || status === 'open' || status === '';
                if (tid && isTodo && !selectedIds.has(tid)) {
                    list.push({
                        value: tid,
                        label: `${getSprintTaskNumberLabel(t)} - ${t.title || t.name || 'Task'}`,
                        sprint: s,
                        task: t
                    });
                }
            }
        }
        return list;
    }, [allSprints, taskRows]);

    const handleSelectTask = (taskId) => {
        const item = availableTasks.find(x => x.value === taskId);
        if (item && addToConsultation) {
            addToConsultation({
                type: 'task',
                id: item.value,
                name: item.task?.title ?? item.task?.name ?? 'Task',
                sprintId: item.sprint?.id,
                fullSprint: item.sprint,
                fullTask: item.task,
            });
        }
    };

    const afterDescriptionWithConsultants = (
        <>
            <div style={{ marginBottom: 16 }}>
                <PanelSectionLabel
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        marginBottom: 8,
                        fontSize: 11,
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                        opacity: 0.75,
                    }}
                >
                    <FileOutlined />
                    Select Tasks
                </PanelSectionLabel>
                <Select
                    showSearch
                    placeholder="Search and select tasks from active sprints..."
                    style={{ width: '100%' }}
                    value={null}
                    onChange={handleSelectTask}
                    options={availableTasks}
                    filterOption={(input, option) =>
                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                />
            </div>

            <div style={{ marginBottom: 16 }}>
                <PanelSectionLabel
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        marginBottom: 8,
                        fontSize: 11,
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                        opacity: 0.75,
                    }}
                >
                    <UserOutlined />
                    Select Consultants
                </PanelSectionLabel>
                <Select
                    mode="multiple"
                    placeholder="Select users for consultation..."
                    style={{ width: '100%' }}
                    value={consultantIds}
                    onChange={setConsultantIds}
                    options={consultantOptions}
                    filterOption={(input, option) =>
                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                />
            </div>
            
            <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <PanelSectionLabel
                    style={{
                        marginBottom: 0,
                        fontSize: 11,
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                        opacity: 0.75,
                    }}
                >
                    QA Passed
                </PanelSectionLabel>
                <Switch 
                    checked={qaPassed} 
                    onChange={setQaPassed} 
                    size="small"
                />
            </div>

            {attachmentsBlock}
        </>
    );

    return (
        <>
            <MilestoneLinkModal
                open={consultationLinkModalOpen}
                onCancel={closeConsultationLinkModal}
                linkModalForm={consultationLinkForm}
                handleLinkModalSubmit={handleConsultationLinkModalSubmit}
            />
            <ProjectSubmissionModal
                open={open}
                onCancel={onCancel}
                modalSize="md"
                variant="qa"
                submissionTitle="Consultation"
                descriptionHtml={consultationNotes}
                onDescriptionChange={setConsultationNotes}
                isAttachment={true}
                isLink={true}
                isMention={true}
                mentionUsers={mentionUsers}
                mentionMilestones={mentionMilestones}
                mentionSprints={mentionSprints}
                richTextEditorProps={richTextEditorProps}
                afterDescription={afterDescriptionWithConsultants}
                taskRows={taskRows}
                onTaskRowClick={handleTaskClick}
                onRemoveTask={removeFromConsultation}
                onSubmit={submitConsultation}
                submitPending={submitPending}
                submitButtonLabel="Create Consultation"
                emptyTasksMessage="No task selected for consultation."
            />
        </>
    );
}
